'use client';

/**
 * usePurchaseFlowHandlers Hook
 *
 * Extracts and organizes all event handlers for the PurchaseFlow component.
 * Separates business logic from the main component for better maintainability.
 *
 * Grouped handlers:
 * - Navigation handlers (back, close)
 * - Payment method handlers (select, add, edit)
 * - Payment processing handlers (pay, complete, failed)
 * - Data handlers (reload)
 *
 * @example
 * ```tsx
 * const handlers = usePurchaseFlowHandlers({
 *   analytics,
 *   navigation,
 *   paymentData,
 *   options
 * });
 *
 * // Use in JSX
 * <button onClick={handlers.handlePayClick}>Pay</button>
 * ```
 */

import { useCallback } from 'react';
import type {
  PaymentMethod, PaymentMethodKind, CloseReason, FetchDataParams, CompletionResult
} from '../types';
import type { UseNavigationResult } from './useNavigation';
import type { UsePurchaseAnalyticsResult } from '../analytics';
import { SCREENS } from '../utils/constants';
import { getAddBehavior } from '../config/gatewayRegistry';
import { isWebProcessedPayment } from '../gateways/componentSelector';
import { addPaymentSource, setPaymentSourceAsDefault } from '../api';
import { mapPaymentMethodFromApi } from '../utils/dataTransformers';

// Re-export FetchDataParams for convenience
export type { FetchDataParams } from '../types';

// ============================================================================
// Types
// ============================================================================

/** Dependencies required by the handlers hook */
export interface UsePurchaseFlowHandlersDeps {
  /** Analytics hook instance */
  analytics: UsePurchaseAnalyticsResult;
  /** Navigation hook result */
  navigation: UseNavigationResult;
  /** Payment data state and methods */
  paymentData: {
    selectedPaymentMethod: PaymentMethod | null;
    paymentMethods: PaymentMethod[];
    /** Extra params from /combined `current` adding items, keyed by kind (e.g. { pp: 'force_add_kind=pp' }) */
    addingExtraParams: Record<string, string>;
    fetchData: (price: number, bonus: number, isPayg?: boolean, creditsRequested?: number) => Promise<void>;
    setSelectedPaymentMethod: (method: PaymentMethod | null) => void;
    updatePaymentMethods: (methods: PaymentMethod[], selectedMethod: PaymentMethod | null) => void;
  };
  /** Handler options */
  options: {
    isPayg: boolean;
    fetchParams: FetchDataParams;
    /** Callback when purchase completes */
    onComplete?: (result?: CompletionResult) => void;
    /** Callback when purchase fails */
    onFailed?: (error: string) => void;
    /** Callback when user cancels/closes */
    onCancel?: (reason?: CloseReason) => void;
    /** Called when PAYG validation should start */
    onStartValidation?: () => void;
    /** Called after the default payment method is successfully changed (triggers toast in PurchaseFlow) */
    onDefaultChanged?: () => void;
    /** Called when user starts a delegated payment (no processing screen shown; flow uses isWebProcessedPayment to detect) */
    onDelegatedPaymentStarted?: (kind: string) => void;
  };
}

/** Return type for usePurchaseFlowHandlers */
export interface UsePurchaseFlowHandlersResult {
  // Navigation handlers
  handleClose: (reason?: CloseReason) => void;
  handleBackClick: () => void;
  handleCloseClick: (processingHasError: boolean, setProcessingHasError: (value: boolean) => void) => void;

  // Payment method handlers
  handlePaymentMethodClick: () => void;
  handleAddPaymentMethodClick: (kind: string, setAddingKind: (value: PaymentMethodKind | null) => void, startProcessingCallback: () => void) => Promise<void>;
  handlePaymentMethodSelect: (paymentMethod: PaymentMethod) => Promise<void>;
  handlePaymentMethodAdded: (
    setAddingKind: (value: PaymentMethodKind | null) => void
  ) => Promise<void>;

  // Payment processing handlers
  handlePayClick: () => void;
  handlePaymentComplete: (result?: CompletionResult) => void;
  handlePaymentFailed: (errorMessage: string) => void;

  // Error handler
  handleError: (errorMessage: string) => void;
}

// ============================================================================
// Hook Implementation
// ============================================================================

export const usePurchaseFlowHandlers = ({
  analytics,
  navigation,
  paymentData,
  options
}: UsePurchaseFlowHandlersDeps): UsePurchaseFlowHandlersResult => {
  const {
    navigateTo, goBack, reset: resetNavigation, currentScreen
  } = navigation;
  const {
    selectedPaymentMethod, paymentMethods, addingExtraParams, fetchData, setSelectedPaymentMethod, updatePaymentMethods
  } = paymentData;
  const {
    isPayg, fetchParams, onComplete, onFailed, onCancel, onStartValidation,
    onDefaultChanged, onDelegatedPaymentStarted
  } = options;

  // ==========================================================================
  // Error Handler
  // ==========================================================================

  const handleError = useCallback((errorMessage: string) => {
    // eslint-disable-next-line no-console
    console.error('Purchase flow error:', errorMessage);
  }, []);

  // ==========================================================================
  // Navigation Handlers
  // ==========================================================================

  const handleClose = useCallback((reason: CloseReason = 'user_closed') => {
    onCancel?.(reason);
  }, [onCancel]);

  const handleBackClick = useCallback(() => {
    goBack(() => handleClose('user_closed'));
  }, [goBack, handleClose]);

  const handleCloseClick = useCallback((
    processingHasError: boolean,
    setProcessingHasError: (value: boolean) => void
  ) => {
    if (currentScreen === SCREENS.PAYMENT_PROCESSING && processingHasError) {
      setProcessingHasError(false);
      goBack();
    } else {
      handleClose('user_closed');
    }
  }, [currentScreen, goBack, handleClose]);

  // ==========================================================================
  // Payment Method Handlers
  // ==========================================================================

  const handlePaymentMethodClick = useCallback(() => {
    navigateTo(SCREENS.PAYMENT_METHODS_LIST);
  }, [navigateTo]);

  const handleAddPaymentMethodClick = useCallback(async (
    kind: string,
    setAddingKind: (value: PaymentMethodKind | null) => void,
    startProcessingCallback: () => void
  ): Promise<void> => {
    analytics.trackPaymentMethodClicked(kind);
    const methodKind = kind as PaymentMethodKind;
    const addConfig = getAddBehavior(kind);

    // Kinds that require a dedicated setup screen (e.g., CC with Checkout Frames form).
    // Navigate to the add screen and let the screen component handle the rest.
    if (addConfig.requiresSetupScreen) {
      const prev = selectedPaymentMethod
        ? { kind: selectedPaymentMethod.kind, id: selectedPaymentMethod.id, gatewayName: selectedPaymentMethod.gatewayName }
        : undefined;
      analytics.trackStartAddingCard(kind, prev);
      setAddingKind(methodKind);
      navigateTo(SCREENS.ADD_CREDIT_CARD);
      return;
    }

    // IAP: create a local payment method without API call.
    // IAP is never a default payment method from the API — it only appears as an
    // "add" button. When selected, the user sees the IAP card and buy button first,
    // then clicking buy sends `on_pay` to the native app via ChargeIAP.
    if (methodKind === 'iap') {
      const localMethod: PaymentMethod = {
        id: 0,
        kind: 'iap',
        gatewayName: 'iap',
        isDefault: true // Marks as active selection so the pay button renders
      };
      setSelectedPaymentMethod(localMethod);

      if (selectedPaymentMethod) {
        // From PaymentMethodsListScreen — go back to purchase details
        resetNavigation();
      }
      // else: From PurchaseDetailsScreen — stay and re-render with IAP card and Pay button
      return;
    }

    // When the user has no saved method and the kind starts processing immediately
    // (e.g. PayPal popup), skip the POST /payment_sources API call — the processor
    // only needs extraParams (e.g. force_add_kind=pp) and the shared metadata props,
    // never the payment source id or gateway name.
    const extraParams = addingExtraParams[methodKind];
    if (!selectedPaymentMethod && addConfig.shouldStartProcessingDirectly) {
      const localMethod: PaymentMethod = {
        id: 0, kind: methodKind, gatewayName: methodKind, isDefault: true, extraParams
      };
      setSelectedPaymentMethod(localMethod);
      analytics.setPaymentOption({ kind: methodKind });
      navigateTo(SCREENS.PAYMENT_PROCESSING);
      startProcessingCallback();
      return;
    }

    // Kinds without a setup screen: add source directly via API and update selection.
    try {
      const response = await addPaymentSource(methodKind);
      const { id, gateway_name: gatewayName } = response.source;

      // Use the option from the response so we get full context (e.g. Apple Pay session config)
      const addedOption = response.options?.find((o) => o.id === id);
      const newMethod: PaymentMethod = addedOption
        ? { ...mapPaymentMethodFromApi(addedOption), isDefault: true, extraParams }
        : {
          id, kind: methodKind, gatewayName, isDefault: true, extraParams
        };

      const updatedMethods = [...paymentMethods, newMethod];
      updatePaymentMethods(updatedMethods, newMethod);

      if (selectedPaymentMethod) {
        // From PaymentMethodsListScreen — go back to purchase details
        resetNavigation();
      }
    } catch (err) {
      handleError(err instanceof Error ? err.message : `Failed to add ${ kind } payment method`);
    }
  }, [navigateTo, resetNavigation, analytics, selectedPaymentMethod, paymentMethods, addingExtraParams, setSelectedPaymentMethod, updatePaymentMethods, handleError]);

  const handlePaymentMethodSelect = useCallback(async (paymentMethod: PaymentMethod) => {
    analytics.trackPaymentMethodClicked(paymentMethod.kind);

    // Skip API call if already the default payment method
    if (selectedPaymentMethod?.id === paymentMethod.id && paymentMethod.isDefault) {
      goBack();
      return;
    }

    try {
      if (selectedPaymentMethod) {
        analytics.updateConfig({
          prevPaymentMethod: {
            kind: selectedPaymentMethod.kind,
            id: selectedPaymentMethod.id,
            gatewayName: selectedPaymentMethod.gatewayName
          }
        });
      }
      const response = await setPaymentSourceAsDefault(paymentMethod.id);
      const updatedMethods = (response.sources?.existing || []).map(mapPaymentMethodFromApi);
      const selectedMethod = updatedMethods.find(m => m.id === paymentMethod.id) || paymentMethod;

      analytics.trackPaymentMethodChanged(paymentMethod.kind, paymentMethod.id, paymentMethod.label);
      updatePaymentMethods(updatedMethods, selectedMethod);
      onDefaultChanged?.();
      goBack();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to set payment method as default';
      handleError(errorMessage);
    }
  }, [selectedPaymentMethod, goBack, updatePaymentMethods, handleError, analytics, onDefaultChanged]);

  const handlePaymentMethodAdded = useCallback(async (
    setAddingKind: (value: PaymentMethodKind | null) => void
  ) => {
    // Only CC comes through here now (all other kinds are handled directly
    // in handleAddPaymentMethodClick). Refetch payment data from API
    // so the newly added card appears in the list.
    const { price, bonus, creditsRequested } = fetchParams;
    await fetchData(price, bonus, isPayg, creditsRequested);

    // Navigate away BEFORE clearing the kind so the add-card screen
    // doesn't briefly render empty content with the header still visible.
    resetNavigation();
    setAddingKind(null);
  }, [fetchData, fetchParams, resetNavigation]);

  // ==========================================================================
  // Payment Processing Handlers
  // ==========================================================================

  const handlePayClick = useCallback(() => {
    if (!selectedPaymentMethod) {
      handleError('No payment method selected');
      return;
    }

    // Check PAYG compatibility (uses backend's eligible_to_pay_as_you_go flag)
    if (isPayg && selectedPaymentMethod.eligibleToPayAsYouGo === false) {
      handleError(`${ selectedPaymentMethod.label || selectedPaymentMethod.kind } is not available for this service`);
      return;
    }

    const ccType = typeof selectedPaymentMethod.context?.type === 'string'
      ? selectedPaymentMethod.context.type
      : selectedPaymentMethod.label;
    analytics.setPaymentOption({
      kind: selectedPaymentMethod.kind,
      gatewayName: selectedPaymentMethod.gatewayName,
      id: selectedPaymentMethod.id,
      ccType
    });

    // Navigate to appropriate screen (skip for delegated payments — native app handles UI)
    if (isPayg) {
      const callback = selectedPaymentMethod.requiresPayAsYouGoValidation === false ? onComplete : onStartValidation;
      callback?.();
      return;
    }

    if (currentScreen === SCREENS.PAYMENT_PROCESSING) return;

    if (!isWebProcessedPayment(selectedPaymentMethod.kind)) {
      onDelegatedPaymentStarted?.(selectedPaymentMethod.kind);
      return;
    }

    navigateTo(SCREENS.PAYMENT_PROCESSING);
  }, [selectedPaymentMethod, currentScreen, navigateTo, handleError, analytics, isPayg, onComplete, onStartValidation, onDelegatedPaymentStarted]);

  const handlePaymentComplete = useCallback((result?: CompletionResult) => {
    onComplete?.(result);
  }, [onComplete]);

  const handlePaymentFailed = useCallback((errorMessage: string) => {
    onFailed?.(errorMessage);
    goBack();
  }, [onFailed, goBack]);

  // ==========================================================================
  // Return All Handlers
  // ==========================================================================

  return {
    // Navigation
    handleClose,
    handleBackClick,
    handleCloseClick,

    // Payment method
    handlePaymentMethodClick,
    handleAddPaymentMethodClick,
    handlePaymentMethodSelect,
    handlePaymentMethodAdded,

    // Payment processing
    handlePayClick,
    handlePaymentComplete,
    handlePaymentFailed,

    // Error
    handleError
  };
};
